import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import { venueUrl } from '@/lib/urls';
import api from '@/lib/api';

export default function VenuesByAmenity() {
  const { amenity } = useParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const decodedAmenity = decodeURIComponent(amenity || '').replace(/-/g, ' ');

  useEffect(() => {
    api.get(`/venues/by-amenity/${encodeURIComponent(decodedAmenity)}`).then(res => setVenues(res.data.venues || [])).catch(() => {}).finally(() => setLoading(false));
  }, [decodedAmenity]);

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Link to="/venues" className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All Venues</Link>
        <Badge className="mb-4 bg-indigo-600/20 text-indigo-300 border-indigo-500/30 text-lg px-4 py-1">{decodedAmenity}</Badge>
        <h1 className="text-3xl font-bold text-white mb-2">Denver Venues with {decodedAmenity}</h1>
        <p className="text-slate-400 mb-8">{venues.length} {venues.length === 1 ? 'venue' : 'venues'} found</p>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : venues.length === 0 ? (
          <div className="text-center py-20"><Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No venues with this feature yet.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map(venue => (
              <Link key={venue.id} to={venueUrl(venue)}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all overflow-hidden group">
                  <div className="aspect-video bg-slate-700 overflow-hidden">
                    {venue.images?.[0] ? <img src={venue.images[0]} alt={venue.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-12 h-12 text-slate-600" /></div>}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-white text-lg">{venue.name}</h3>
                    <div className="flex items-center gap-2 mt-1"><Badge variant="secondary" className="text-xs">{venue.venue_type || venue.category}</Badge></div>
                    {venue.address && <p className="text-sm text-slate-400 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {venue.address}</p>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
